import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Subject } from 'rxjs/Subject';
import { InvoicePayment, PageRequest } from '@models/models';
import { MessageService } from '@services/helpers/message-service/message.service';
import { DashboardDataServiceProvider } from '@services/dashboard/dashboard.data.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { PaymentsDataServiceProvider } from '@services/payments/payments-data-service/payments.data.service';
import { filter, takeUntil } from 'rxjs/operators';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';

@Component({
	selector: 'upcoming-payments-block',
	template: require('./upcoming-payments-block.html'),
	styles: [require('./upcoming-payments-block.scss')]
})
export class UpcomingPaymentsBlockComponent implements OnInit, OnDestroy {
	@Input() buttonDisabled: boolean;
	@Input() spinningWheelActive: boolean;
	unsubscribe: Subject<void> = new Subject();

	payments: InvoicePayment[] = [];
	itemsQuantity: number;
	pagesQuantity: number;
	pageRequest: PageRequest;
	leadForDelete: number;
	selectedPayment: InvoicePayment;
	paymentToWriteOffId: number;
	clientLink: any;

	readonly PAGE_REQUEST_DEFAULT: PageRequest = {
		paging: {
			pageNumber: 0,
			pageSize: 10
		},
		sortCriterias: [
			{
				fieldName: 'paymentDate',
				sortAscending: true
			}
		],
		search: '',
		filterBy: 'unpaid'
	};

	private allowedFilters: string[];

	constructor(
		private dashboardDataService: DashboardDataServiceProvider,
		private messageService: MessageService,
		private location: Location,
		private timeService: TimeServiceProvider,
		private brandsDataService: BrandsDataServiceProvider,
		private paymentsDataService: PaymentsDataServiceProvider
	) {}

	ngOnInit(): void {
		this.configureCommunicationAdapter();
		this.subscribeToBrandsChange();
		this.pageRequest = this.PAGE_REQUEST_DEFAULT;
		this.getData();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	updateData(pageRequest: PageRequest): void {
		this.pageRequest = pageRequest;
		this.dashboardDataService
			.getUpcomingPaymentsData(pageRequest)
			.subscribe(
				(data): void => this.successGetData(data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	showRecordPaymentModal(payment: InvoicePayment): void {
		this.selectedPayment = payment;
		$('#markAsPayedModal').modal('show');
	}

	getPayOnlineLink(id): void {
		this.paymentsDataService.getPayOnlineLink(id).subscribe(
			(result): void => {
				this.clientLink = result.link;
			},
			(error): void => {
				this.messageService.showDefaultErrorMessage(error);
			}
		);
	}

	showWriteOff(id: number): void {
		this.paymentToWriteOffId = id;
		$('#writeOffModal').modal('show');
	}

	showInvoice(invoiceId: number): void {
		this.location.go(`/invoices/${invoiceId}`);
	}

	isFilterAllowed(filter: string) {
		if (!this.allowedFilters) {
			return false;
		}

		return this.allowedFilters.includes(filter);
	}

	orderByField(filter: string) {
		if (this.pageRequest.sortCriterias[0].fieldName === filter) {
			this.pageRequest.sortCriterias[0].sortAscending = !this.pageRequest
				.sortCriterias[0].sortAscending;
		} else {
			this.pageRequest.sortCriterias[0].fieldName = filter;
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}
		this.pageRequest.paging.pageNumber = 0;

		this.getData();
	}

	updateModel(resetPagination: boolean): void {
		if (resetPagination) {
			this.paginationToDefaults();
		} else {
			this.getData();
		}
	}

	updateRecentLeadsModel(resetPagination: boolean): void {
		window.snCommunicationAdapter.callMethod(
			'updateRecentLeadsModel',
			resetPagination
		);
	}

	updateJobTasksModel(resetPagination: boolean): void {
		window.snCommunicationAdapter.callMethod(
			'updateJobTasksModel',
			resetPagination
		);
	}

	private getData(): void {
		this.dashboardDataService
			.getUpcomingPaymentsData(this.pageRequest)
			.subscribe(
				(data): void => this.successGetData(data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private successGetData(data): void {
		this.pageRequest.paging.searchToken = data.searchToken;
		this.itemsQuantity = data ? data.totalSize : 0;
		this.pagesQuantity = Math.ceil(
			this.itemsQuantity / this.pageRequest.paging.pageSize
		);
		this.payments = JSON.parse(JSON.stringify(data.items));
		this.allowedFilters = data.sortingFields;

		this.handlePayments();
	}

	private paginationToDefaults(): void {
		this.pageRequest = this.PAGE_REQUEST_DEFAULT;
		delete this.pageRequest.paging.searchToken;
		this.getData();
	}

	private handlePayments(): void {
		if (this.payments) {
			this.payments.forEach((payment): void => {
				payment.days = this.calcDate(
					new Date(),
					this.timeService.createDateAsUTC(payment.due)
				);
				let dayMulti = ' days';
				if (payment.days === 1 || payment.days === -1) {
					dayMulti = ' day';
				}
				if (payment.days === 0) {
					payment.days = 'today';
				} else if (payment.days > 0) {
					payment.days = `${payment.days + dayMulti} ago`;
					payment.isLate = true;
				} else {
					payment.days = `in ${-payment.days}${dayMulti}`;
				}
			});
		}
	}

	private calcDate(date1, date2): number {
		const diff = Math.floor(date1.getTime() - date2.getTime());
		const day = 1000 * 60 * 60 * 24;

		return Math.floor(diff / day);
	}

	private configureCommunicationAdapter(): void {
		window.snCommunicationAdapter.registerMethod(
			'updateUpcomingPaymentsModel',
			this.updateModel.bind(this)
		);
	}

	private updateGraphsModel = function(tabs) {
		window.snCommunicationAdapter.callMethod('updateGraphsModel', tabs);
	};

	private subscribeToBrandsChange(): void {
		this.brandsDataService.brandsChange
			.pipe(
				takeUntil(this.unsubscribe),
				filter((data): boolean => !!data)
			)
			.subscribe((): void => this.getData());
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./web/modern/components/dashboard/upcoming-payments-block/upcoming-payments-block.ts